import { type FC, useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { UserRoundPlus, X } from 'lucide-react'

interface AddRoleModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSubmit?: (payload: {
        title: string
        description: string
        isActive?: boolean
    }) => void
    initialTitle?: string
    initialDescription?: string
    isSubmitting?: boolean
    initialIsActive?: boolean
    isLoadingDetails?: boolean
}

export const AddRoleModal: FC<AddRoleModalProps> = ({
    open,
    onOpenChange,
    onSubmit,
    initialTitle = '',
    initialDescription = '',
    isSubmitting = false,
    initialIsActive = true,
    isLoadingDetails = false,
}) => {
    const [title, setTitle] = useState(initialTitle)
    const [description, setDescription] = useState(initialDescription)
    const [isActive, setIsActive] = useState(initialIsActive)
    const isEditing = Boolean(initialTitle)
    const canSubmit = Boolean(title.trim())

    // Sync state when initial values change (e.g., when editing a different role)
    useEffect(() => {
        setTimeout(() => {
            setTitle(initialTitle)
            setDescription(initialDescription)
            setIsActive(initialIsActive)
        }, 0)
    }, [initialTitle, initialDescription, initialIsActive])

    const handleOpenChange = (nextOpen: boolean) => {
        setTitle(initialTitle)
        setDescription(initialDescription)
        setIsActive(initialIsActive)
        onOpenChange(nextOpen)
    }

    const handleSubmit = () => {
        if (!canSubmit || isSubmitting) return
        onSubmit?.({
            title: title.trim(),
            description: description.trim(),
            ...(isEditing && { isActive }),
        })
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                showCloseButton={false}
                className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100%-1rem)] -translate-x-1/2 -translate-y-1/2 overflow-hidden border-0 bg-transparent p-0 shadow-2xl data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-xl sm:rounded-2xl"
            >
                <div className="flex flex-col overflow-hidden rounded-2xl bg-white">
                    <div className="flex items-start justify-between gap-3 border-b px-6 py-4">
                        <div className="flex items-start gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#E5F5F9]">
                                <UserRoundPlus className="h-5 w-5 text-[#0F172A]" />
                            </div>
                            <div>
                                <h2 className="text-lg font-semibold text-[#0F172A]">
                                    {isEditing ? 'Edit role' : 'Add new role'}
                                </h2>
                                <p className="text-sm text-muted-foreground">
                                    {isEditing
                                        ? 'Update the role details for your admin team.'
                                        : 'Add to the list of roles you have at Ogaboss.'}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenChange(false)}
                            className="h-8 w-8 rounded-full"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="space-y-4 px-6 py-5">
                        <Input
                            value={title}
                            placeholder="Fill in role title"
                            onChange={e => setTitle(e.target.value)}
                            disabled={isSubmitting || isLoadingDetails}
                            className="h-[52px] rounded-xl"
                        />
                        <Textarea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            disabled={isSubmitting || isLoadingDetails}
                            placeholder="Add a short description for this role"
                            className="min-h-[120px]"
                        />
                        {isEditing && (
                            <div className="flex items-center justify-between rounded-xl border px-4 py-3">
                                <div>
                                    <p className="text-sm font-medium text-[#0F172A]">
                                        Role status
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        Toggle to set this role as active or
                                        inactive.
                                    </p>
                                </div>
                                <Switch
                                    checked={isActive}
                                    onCheckedChange={value =>
                                        setIsActive(Boolean(value))
                                    }
                                    disabled={isSubmitting || isLoadingDetails}
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-3 border-t px-6 py-4 sm:flex-row sm:justify-end sm:gap-4">
                        <Button
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={isSubmitting}
                            className="h-12 flex-1 rounded-full sm:flex-none sm:min-w-[140px]"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={!canSubmit || isSubmitting}
                            className="h-12 flex-1 rounded-full bg-[#17A2B8] text-white hover:bg-[#1493a7] sm:flex-none sm:min-w-[140px]"
                        >
                            {isSubmitting
                                ? 'Saving...'
                                : isEditing
                                  ? 'Save changes'
                                  : 'Add role'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
